import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  userService,
  type UserFilters,
  type CreateUserRequest,
  type UpdateUserRequest,
  type User,
} from '@/services/userService'

export type { User, UserFilters, CreateUserRequest, UpdateUserRequest }

export function useUsers(filters: UserFilters = {}) {
  return useQuery({
    queryKey: ['users', filters],
    queryFn: async () => {
      const response = await userService.getUsers(filters)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch users')
      }
      return response.data
    },
  })
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['users', id],
    queryFn: async () => {
      const response = await userService.getUserById(id)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch user')
      }
      return response.data
    },
    enabled: !!id,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateUserRequest) => {
      const response = await userService.createUser(data)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create user')
      }
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateUserRequest }) => {
      const response = await userService.updateUser(id, data)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update user')
      }
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['users', variables.id] })
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await userService.deleteUser(id)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete user')
      }
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useCheckAffiliateCode() {
  return useMutation({
    mutationFn: async (code: string) => {
      const response = await userService.checkAffiliateCode(code)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to check affiliate code')
      }
      return response.data
    },
  })
}

export function useResetUserTOTP() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await userService.resetUserTOTP(userId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to reset TOTP')
      }
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}
